import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Table,
  Space,
  Row,
  Col,
  Select,
  Modal,
} from 'antd'
import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getMenu,
  saveMenu,
  updateMenu,
  getPage,
} from '../../APIComponents/AdminApi'
import { toast } from 'react-toastify'
import axiosInstance from '../../../Utils/axiosInstance'

const { Option } = Select

const MenuMaster = () => {
  //============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Menu Master')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1

  //============================================

  const [form] = Form.useForm()
  const [menus, setMenus] = useState([])
  const [loading, setLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedMenu, setSelectedMenu] = useState(null)

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  const handleView = (record) => {
    setSelectedMenu(record)
    setViewModalVisible(true)
  }

  // Fetch All Menus
  const fetchMenus = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(getMenu)
      setMenus(res.data)
    } catch (err) {
      toast.error('Failed to fetch menus')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMenus()
  }, [])

  // Add / Update Menu
  const onFinish = async (values) => {
    const objData = {
      menu_name: values.menuName,
    }

    try {
      if (editingId) {
        setUpdateLoading(true)
        const res = await axiosInstance.put(
          `${updateMenu}/${editingId}`,
          objData,
        )
        if (res.data === 'UPDATED') {
          toast.success('Menu updated successfully')
          fetchMenus()
        } else if (res.data === 'ALREADYEXIST') {
          toast.warn('Menu already exists!')
        }
      } else {
        setAddLoading(true)
        const res = await axiosInstance.post(saveMenu, objData)
        if (res.data === 'SAVED') {
          toast.success('Menu added successfully')
          fetchMenus()
        } else if (res.data === 'ALREADYEXIST') {
          toast.warn('Menu already exists!')
        }
      }

      form.resetFields()
      setEditingId(null)
    } catch (err) {
      toast.error('Operation failed. Please try again.')
    } finally {
      setAddLoading(false)
      setUpdateLoading(false)
    }
  }

  // Edit
  const handleEdit = (record) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setEditingId(record.id)

    form.setFieldsValue({
      menuName: record.menu_name,
    })
  }

  // Cancel
  const handleCancel = () => {
    setEditingId(null)
    form.resetFields()
  }

  // Table Columns
  const columns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    {
      title: 'Menu Name',
      dataIndex: 'menu_name',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Actions',
            width: '15%',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Menu Master</h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* Menu Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              style={{ marginBottom: '20px' }}
            >
              <Row gutter={16} className="pt-3">
                <Col xs={24} md={6}>
                  <Form.Item
                    label="Menu Name"
                    name="menuName"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Enter Menu Name" />
                  </Form.Item>
                </Col>

                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item>
                    <Space>
                      {!editingId ? (
                        <>
                          {pagePermission.is_new == 1 && (
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              loading={addLoading}
                            >
                              Add
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<EditOutlined />}
                            loading={updateLoading}
                          >
                            Update
                          </Button>
                          <Button onClick={handleCancel}>Cancel</Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={menus}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: menus.length,
                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize: pageSize,
                  })
                },
              }}
              bordered
              loading={loading}
              className="shadow rounded custom-table p-0"
              style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
            />
          </div>

          {/* ======== View Modal ====== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Menu Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            centered
          >
            <div
              style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f5f7fa',
                border: '1px solid #e6e6e6',
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Menu Name :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedMenu?.menu_name}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default MenuMaster
